import React, { useState, useEffect, useRef } from 'react';
import { searchProducts } from '../api';

function ProductSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState([]);
  const [selectedFacets, setSelectedFacets] = useState({});
  const [sortBy, setSortBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibleJsonIds, setVisibleJsonIds] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [foundCount, setFoundCount] = useState(0);
  const perPage = 10;

  const debounceTimeout = useRef(null);

  const buildFacetQuery = () => {
    const facetParams = [];
    for (const field in selectedFacets) {
      if (selectedFacets[field].length > 0) {
        const values = selectedFacets[field].map((val) => `"${val}"`).join(',');
        facetParams.push(`${field}:=[${values}]`);
      }
    }
    return facetParams.join(' && ');
  };

  const handleSearch = async (page = 1, currentQuery = query, currentSortBy = sortBy) => {
    if (!currentQuery.trim()) {
      setResults([]);
      setFacets([]);
      setFoundCount(0);
      return;
    }

    setLoading(true);
    try {
      const filters = buildFacetQuery();
      const { data } = await searchProducts(currentQuery, page, perPage, filters, currentSortBy);
      setResults(data.payload.hits || []);
      setFacets(data.payload.facet_counts || []);
      setFoundCount(data.payload.found || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error('Search Error:', err);
      alert('Search failed');
    }
    setLoading(false);
  };

  // Debounced search on query change
  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      handleSearch(1, query);
    }, 300);
    return () => clearTimeout(debounceTimeout.current);
  }, [query]);

  // Trigger search on facet filter change
  useEffect(() => {
    handleSearch(1, query);
  }, [selectedFacets]);

  const toggleJsonVisibility = (id) => {
    setVisibleJsonIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFacetToggle = (field, value) => {
    setSelectedFacets((prev) => {
      const currentValues = prev[field] || [];
      const isSelected = currentValues.includes(value);
      const updatedValues = isSelected
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [field]: updatedValues,
      };
    });
  };

  const totalPages = Math.ceil(foundCount / perPage);

  return (
    <div style={{ padding: '2rem' }}>
      <h1>QOIPR Products - IntelliSearch</h1>

      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search product specifications..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '300px' }}
        />

        <select value={sortBy} onChange={(e) => {setSortBy(e.target.value); handleSearch(1, query, e.target.value);}} style={{ marginLeft: '1rem' }}>
          <option value="">Sort By</option>
          <option value="MfPN:asc">MfPN A-Z</option>
          <option value="MfPN:desc">MfPN Z-A</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '2rem' }}>
        {/* LEFT PANEL - FACETS */}
        <div style={{ minWidth: '250px', borderRight: '1px solid #ccc', paddingRight: '1rem' }}>
          <h3>Filters</h3>
          {facets.length > 0 ? (
            facets.map((facet) => (
              <div key={facet.field_name} style={{ marginBottom: '1rem' }}>
                <strong>{facet.field_name}</strong>
                <ul style={{ listStyleType: 'none', paddingLeft: '1rem' }}>
                  {facet.counts.map((item) => (
                    <li key={item.value}>
                      <label>
                        <input
                          type="checkbox"
                          checked={(selectedFacets[facet.field_name] || []).includes(item.value)}
                          onChange={() => handleFacetToggle(facet.field_name, item.value)}
                        />{' '}
                        {item.value} ({item.count})
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <p>No facet data.</p>
          )}
        </div>

        {/* RIGHT PANEL - RESULTS */}
        <div style={{ flexGrow: 1 }}>
          <h3>Search Record Count = {foundCount}</h3>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={() => handleSearch(currentPage - 1)}
                disabled={currentPage <= 1}
                style={{ marginRight: '1rem' }}
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => handleSearch(currentPage + 1)}
                disabled={currentPage >= totalPages}
                style={{ marginLeft: '1rem' }}
              >
                Next
              </button>
            </div>
          )}

          {results.length > 0 ? (
            <ul style={{ paddingLeft: 0 }}>
              {results.map((hit) => (
                <li
                  key={hit.document.id}
                  style={{
                    border: '1px solid #ddd',
                    padding: '1rem',
                    marginBottom: '1rem',
                    listStyleType: 'none'
                  }}
                >
                  <div><strong>Manufacturer:</strong> {hit.document.Manufacturer}</div>
                  <div><strong>Category: {hit.document.CatID} -</strong> {hit.document.category}</div>
                  <div><strong>MfPN:</strong> {hit.document.MfPN}</div>
                  <div><strong>Title:</strong> {JSON.parse(hit.document.prodJson).Product[0].Title || ''}</div>

                  <button onClick={() => toggleJsonVisibility(hit.document.id)}>
                    {visibleJsonIds[hit.document.id] ? 'Hide ProdJson' : 'View ProdJson'}
                  </button>

                  {visibleJsonIds[hit.document.id] && (
                    <pre
                      style={{
                        marginTop: '1rem',
                        background: '#f4f4f4',
                        padding: '1rem',
                        borderRadius: '5px',
                        overflowX: 'auto'
                      }}
                    >
                      {JSON.stringify(JSON.parse(hit.document.prodJson), null, 4)}
                    </pre>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No results found.</p>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={() => handleSearch(currentPage - 1)}
                disabled={currentPage <= 1}
                style={{ marginRight: '1rem' }}
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => handleSearch(currentPage + 1)}
                disabled={currentPage >= totalPages}
                style={{ marginLeft: '1rem' }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductSearch;
